import moment from 'moment';
import React, { useState, useEffect, useRef, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import { getDates, days, getWeekArray } from '../utilities/calendarData.js';

import IosArrowBack from 'react-ionicons/lib/IosArrowBack';
import IosArrowForward from 'react-ionicons/lib/IosArrowForward';
import '../dashboard.scss';

// This ref-forwarding wrapper exposes an imperative handle allowing parent to control the month displayed.
function Calendar({ renderView }, ref) {
  const [month, setMonth] = useState(moment());
  const [dates, setDates] = useState(() => getDates(moment()));
  const singleRow = useRef(null);
  const windowWidth = useWidth();
  const cellHeight = useCellHeight(singleRow, dates);

  // Provide an imperative API for parents to call: calendarRef.current.gotoMonthYear(...)
  useImperativeHandle(ref, () => ({
    gotoMonthYear: (year, monthIndex, selectedDate) => {
      // monthIndex expects 0-based (Jan==0), same as moment
      const newMonthMoment = moment({ year, month: monthIndex });
      setMonth(newMonthMoment);
      setDates(getDates(newMonthMoment));
    },
    gotoDate: (dateMoment) => {
      // dateMoment: a moment object
      if (moment.isMoment(dateMoment)) {
        setMonth(dateMoment.clone());
        setDates(getDates(dateMoment));
      }
    }
  }));

  const switchMonth = (previous) => {
    const date = previous ? month.clone().subtract(1, 'month') : month.clone().add(1, 'month');
    setDates(getDates(date));
    setMonth(date);
  };

  const checkMonth = (date) => date.isSame(month, 'month');

  useLayoutEffect(() => {
    if (windowWidth <= 350) {
      setDates(getDates(month, 4));
      return;
    }
    const temp = getDates(month);
    if (temp.length !== dates.length) {
      setDates(temp);
    }
    // eslint-disable-next-line
  }, [windowWidth]);

  return (
    <div className="calendarComponent">
      <div className="sectionTitle calendarTitle">
        <IosArrowBack fontSize="20px" className="switchIcon" onClick={() => switchMonth(true)} />
        {month.format('MMMM YYYY')}
        <IosArrowForward fontSize="20px" className="switchIcon" onClick={() => switchMonth(false)} />
      </div>
      <div className="calendar">
        <div className="calendarWeeks">
          {getViews(getWeekArray(dates), cellHeight, getWeekView)}
        </div>
        <div className="calendarDates">
          {windowWidth > 350 && (
            <div className="calendarRow">
              {getViews(days, cellHeight, getDayView)}
            </div>
          )}
          {dates.map((row, j) => (
            <div className="calendarRow" key={j} ref={!singleRow.current ? singleRow : null}>
              {row.map((date, i) => renderView(date, i, checkMonth(date), cellHeight))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CalendarWithRef = forwardRef(Calendar);

// Helper hooks, below unchanged
function getWeekView(v, i, cellHeight) {
  return (
    <div key={i} className="calendarRow">
      <div className={"calenderDate" + (i === 0 ? " calendarDummy" : " calendarWeek")} style={{ height: cellHeight }}>
        {i === 0 ? '' : 'Week ' + i}
      </div>
    </div>
  );
}

function getDayView(day, i, cellHeight) {
  return (
    <div key={i} className="calenderDate calendarDay" style={{ height: cellHeight }}>
      {day}
    </div>
  );
}

function getViews(data, cellHeight, viewRender) {
  return data.map((item, i) => viewRender(item, i, cellHeight));
}

function useCellHeight(ref, dates) {
  const [height, setHeight] = useState(window.innerWidth);
  const handleResize = () => {
    if (ref && ref.current && ref.current.children && ref.current.children.length > 0) {
      setHeight(ref.current.children[0].offsetWidth);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    handleResize();
    // eslint-disable-next-line
  }, [ref.current, dates]);

  return height;
}

function useWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  const handleResize2 = () => setWidth(window.innerWidth);

  useEffect(() => {
    window.addEventListener('resize', handleResize2);
    return () => {
      window.removeEventListener('resize', handleResize2);
    };
  });

  useEffect(() => {
    handleResize2();
  }, []);

  return width;
}

// Export the component correctly with ref support
export default CalendarWithRef;
